/** @jsxImportSource react */
import React, { useState, useMemo, useEffect } from 'react';
import { useAuth } from '../contexts/auth';
import { api } from '../services/api';
import { Order, OrderStatus, UserRole, PriceCheckRequest } from '../types';
import { useTranslation } from '../i18n';
import { useMutation } from '@tanstack/react-query';
import { toast } from '../components/ui/Toast';
import { ListChecks, DollarSign, Truck, Clock, FileText, Loader2, Inbox } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card, CardContent } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Navigate, useNavigate } from 'react-router-dom';

const PriceCheckForm: React.FC<{ order: Order }> = ({ order }) => {
    const { t } = useTranslation();
    const [price, setPrice] = useState('');
    const [shippingCost, setShippingCost] = useState('');
    const [deliveryTime, setDeliveryTime] = useState('');
    const [notes, setNotes] = useState('');

    const submitMutation = useMutation({
        mutationFn: (priceData: Omit<PriceCheckRequest, 'staffId' | 'status' | 'submittedAt'>) => api.submitPriceCheck(order.id, priceData),
        onSuccess: () => {
            toast.success(t('priceSubmittedSuccess'));
            // Real-time listener will update the UI, so no need to invalidate.
        },
        onError: (error: Error) => {
            toast.error(error.message || t('priceSubmittedError'));
        }
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!price) {
            toast.error(t('priceIsRequired'));
            return;
        }
        submitMutation.mutate({
            price: parseFloat(price),
            shippingCost: parseFloat(shippingCost) || 0,
            deliveryTime,
            notes
        });
    };

    return (
        <form onSubmit={handleSubmit} className="mt-4 space-y-4 p-4 bg-secondary-50 rounded-lg border">
            <h4 className="font-semibold text-secondary-800">{t('submitYourPrice')}</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">{t('price')}*</label>
                    <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-secondary-400" />
                        <Input type="number" value={price} onChange={e => setPrice(e.target.value)} placeholder="e.g. 150.50" required className="ps-9" />
                    </div>
                </div>
                <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">{t('shippingCost')}</label>
                     <div className="relative">
                        <Truck className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-secondary-400" />
                        <Input type="number" value={shippingCost} onChange={e => setShippingCost(e.target.value)} placeholder="e.g. 20.00" className="ps-9" />
                    </div>
                </div>
                <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-secondary-700 mb-1">{t('deliveryTime')}</label>
                     <div className="relative">
                        <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-secondary-400" />
                        <Input type="text" value={deliveryTime} onChange={e => setDeliveryTime(e.target.value)} placeholder={t('deliveryTimePlaceholder')} className="ps-9" />
                    </div>
                </div>
                 <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-secondary-700 mb-1">{t('notes')}</label>
                     <div className="relative">
                        <FileText className="absolute left-3 top-3 h-4 w-4 text-secondary-400" />
                        <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder={t('notesPlaceholder')} rows={3} className="w-full ps-9 p-2 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" />
                    </div>
                </div>
            </div>
             <div className="flex justify-end">
                <Button type="submit" isLoading={submitMutation.isPending}>
                    {t('submitPrice')}
                </Button>
            </div>
        </form>
    );
};


const PriceCheckListPage = () => {
    const { user } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [orders, setOrders] = useState<Order[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (!user || (user.role !== UserRole.Staff && user.role !== UserRole.Admin)) {
            setIsLoading(false);
            return;
        }

        // The `listenToOrders` function for staff members now correctly fetches all relevant orders.
        // We only need to filter for the orders that are currently in the 'PriceChecking' status
        // for this specific page.
        const unsub = api.listenToOrders(user, (allAssignedOrders) => {
            const priceCheckOrders = allAssignedOrders.filter(order => 
                order.status === OrderStatus.PriceChecking
            );
            setOrders(priceCheckOrders);
            setIsLoading(false);
        });

        return () => unsub();
    }, [user]);

    const myPendingRequests = useMemo(() => {
        if (!user) return [];
        return orders.filter(order => {
            const myRequest = order.priceCheckRequests?.find(pcr => pcr.staffId === user.id);
            return myRequest && myRequest.status === 'pending';
        });
    }, [orders, user]);

    if (!user || (user.role !== UserRole.Staff && user.role !== UserRole.Admin)) {
        return <Navigate to="/dashboard" />;
    }
    
    if (isLoading) {
        return (
            <div className="flex h-full items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
            </div>
        );
    }
    
    return (
        <div className="container mx-auto px-4 py-6">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-secondary-900 mb-2 flex items-center gap-3">
                    <ListChecks className="h-8 w-8 text-primary-500" />
                    {t('priceCheckList')}
                </h1>
                <p className="text-secondary-600">{t('priceCheckListDesc')}</p>
            </div>

            {myPendingRequests.length === 0 ? (
                <div className="text-center py-16 px-4 bg-white rounded-xl border">
                    <div className="w-20 h-20 bg-secondary-100 rounded-full mx-auto flex items-center justify-center mb-6"><Inbox className="w-10 h-10 text-secondary-400" /></div>
                    <h3 className="text-xl font-bold text-secondary-900 mb-2">{t('noPendingPriceChecks')}</h3>
                    <p className="text-secondary-500 mb-8 max-w-md mx-auto">{t('allCaughtUpPriceChecks')}</p>
                </div>
            ) : (
                <div className="space-y-6">
                    {myPendingRequests.map(order => (
                         <Card key={order.id} className="overflow-hidden">
                            <CardContent className="p-6">
                               <div className="flex flex-col sm:flex-row justify-between sm:items-start gap-4">
                                    <div>
                                        <p className="text-sm text-secondary-500">{t('orderId')}</p>
                                        <h3 className="text-lg font-bold text-primary-600 cursor-pointer hover:underline" onClick={() => navigate(`/orders/${order.id}`)}>
                                            {order.orderId}
                                        </h3>
                                        <p className="mt-1 font-medium text-secondary-800">{order.productName}</p>
                                    </div>
                                    <div className="text-sm text-secondary-600">
                                        <p><strong>{t('quantity')}:</strong> {order.quantity}</p>
                                        {order.productLink && <a href={order.productLink} target="_blank" rel="noopener noreferrer" className="text-primary-500 hover:underline">{t('productLink')}</a>}
                                    </div>
                               </div>
                               {order.notes && (
                                   <div className="mt-4 p-3 bg-secondary-50 border-s-4 border-secondary-300 rounded-e-lg text-sm text-secondary-700">
                                        <strong>{t('clientNotes')}: </strong>{order.notes}
                                   </div>
                               )}
                               <PriceCheckForm order={order} />
                            </CardContent>
                         </Card>
                    ))}
                </div>
            )}
        </div>
    );
};

export default PriceCheckListPage;